/* Nestaria — shared front-end behaviours
   Lightweight, dependency-free, progressive-enhancement helpers used by the
   custom homepage sections. Loaded with `defer`. */
(function () {
  'use strict';

  /* ---- Scroll reveal ----------------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('.ak-reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Tabs (product-tabs, featured-collection) -------------------------- */
  /* Markup contract:
     [data-ak-tabs]
       [data-ak-tab="<id>"]  (buttons)
       [data-ak-panel="<id>"] (panels) */
  function initTabs() {
    document.querySelectorAll('[data-ak-tabs]').forEach(function (root) {
      var tabs = root.querySelectorAll('[data-ak-tab]');
      var panels = root.querySelectorAll('[data-ak-panel]');
      if (!tabs.length) return;
      function activate(id) {
        tabs.forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-ak-tab') === id);
          t.setAttribute('aria-selected', t.getAttribute('data-ak-tab') === id);
        });
        panels.forEach(function (p) {
          if (p.getAttribute('data-ak-panel') === id) {
            // Non-first panels ship inert inside <template data-ak-lazy> (keeps
            // them out of layout/paint until requested) — stamp on first open.
            var tpl = p.querySelector('template[data-ak-lazy]');
            if (tpl) {
              p.appendChild(tpl.content);
              tpl.remove();
              if (window.AKCarousel) window.AKCarousel();
            }
            p.hidden = false;
          } else {
            p.hidden = true;
          }
        });
      }
      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          activate(t.getAttribute('data-ak-tab'));
          // Recalculate the newly-revealed panel's carousel (was 0-width while hidden)
          var panel = root.querySelector('[data-ak-panel="' + t.getAttribute('data-ak-tab') + '"]');
          var car = panel && panel.querySelector('[data-ak-carousel]');
          if (car && car._akUpdate) car._akUpdate();
        });
      });
    });
  }

  /* ---- Product carousel (product-tabs) ----------------------------------- */
  /* [data-ak-carousel] containing [data-ak-track] + [data-ak-cprev]/[data-ak-cnext].
     One consistent scrolling row; arrows page by the visible width and disable
     at the ends. Hidden entirely when everything already fits. */
  function initProductCarousel() {
    document.querySelectorAll('[data-ak-carousel]').forEach(function (root) {
      if (root._akUpdate) return; // already initialised (re-runs after lazy tab stamping)
      var track = root.querySelector('[data-ak-track]');
      if (!track) return;
      var prev = root.querySelector('[data-ak-cprev]');
      var next = root.querySelector('[data-ak-cnext]');
      var progress = root.querySelector('[data-ak-progress]');
      function step() { return Math.max(track.clientWidth * 0.85, 220); }
      function update() {
        var maxScroll = track.scrollWidth - track.clientWidth - 2;
        var fits = maxScroll <= 2;
        root.classList.toggle('is-static', fits);
        if (prev) prev.disabled = fits || track.scrollLeft <= 2;
        if (next) next.disabled = fits || track.scrollLeft >= maxScroll;
        if (progress && !fits) {
          // Scrollbar-thumb: width = visible fraction, left = scrolled fraction.
          var visFrac = track.clientWidth / track.scrollWidth;
          progress.style.width = (visFrac * 100) + '%';
          progress.style.left = (track.scrollLeft / track.scrollWidth * 100) + '%';
        }
      }
      root._akUpdate = update;
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', function () { window.requestAnimationFrame(update); });
      update();
    });
  }

  /* ---- Countdown timer --------------------------------------------------- */
  /* [data-ak-countdown="2026-12-31T23:59:59"] containing
     [data-unit="days|hours|minutes|seconds"] */
  function initCountdown() {
    var timers = document.querySelectorAll('[data-ak-countdown]');
    if (!timers.length) return;
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var now = Date.now();
      timers.forEach(function (timer) {
        var target = new Date(timer.getAttribute('data-ak-countdown')).getTime();
        var diff = Math.max(0, target - now);
        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var s = Math.floor((diff % 60000) / 1000);
        var map = { days: d, hours: h, minutes: m, seconds: s };
        timer.querySelectorAll('[data-unit]').forEach(function (el) {
          el.textContent = pad(map[el.getAttribute('data-unit')] || 0);
        });
      });
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ---- Fade slider (hero) ------------------------------------------------ */
  /* [data-ak-slider][data-autoplay="5000"]
       [data-ak-slide] (>=1)
       [data-ak-dots] (optional empty container — dots injected here) */
  function initSlider() {
    document.querySelectorAll('[data-ak-slider]').forEach(function (root) {
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-ak-slide]'));
      if (slides.length < 2) return;
      var dotsWrap = root.querySelector('[data-ak-dots]');
      var current = 0;
      var dots = [];
      function go(i) {
        current = (i + slides.length) % slides.length;
        slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === current); });
        dots.forEach(function (d, idx) { d.setAttribute('aria-current', idx === current); });
      }
      if (dotsWrap) {
        slides.forEach(function (_, idx) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ak-dot';
          b.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
          b.addEventListener('click', function () { go(idx); restart(); });
          dotsWrap.appendChild(b);
          dots.push(b);
        });
      }
      var prev = root.querySelector('[data-ak-prev]');
      var next = root.querySelector('[data-ak-next]');
      if (prev) prev.addEventListener('click', function () { go(current - 1); restart(); });
      if (next) next.addEventListener('click', function () { go(current + 1); restart(); });
      go(0);
      // Enable the entrance fade only AFTER first paint, so the LCP hero image
      // isn't held at opacity:0 during initial render (which delays LCP).
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { root.classList.add('is-ready'); });
      });
      var delay = parseInt(root.getAttribute('data-autoplay'), 10);
      var timer = null;
      function restart() {
        if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        clearInterval(timer);
        timer = setInterval(function () { go(current + 1); }, delay);
      }
      // Start autoplay only after the first user interaction (or a 12s fallback) so an
      // auto-advancing hero doesn't keep re-triggering LCP during initial load / lab tests.
      var autoplayStarted = false;
      function startAutoplay() {
        if (autoplayStarted || !delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        autoplayStarted = true;
        restart();
      }
      ['scroll', 'pointerdown', 'keydown', 'touchstart', 'mousemove', 'wheel'].forEach(function (ev) {
        window.addEventListener(ev, startAutoplay, { once: true, passive: true });
      });
      // NOTE: no time-based fallback on purpose — auto-advancing before the user
      // interacts re-triggers LCP (carousel churn) and tanks the lab score. Real
      // users virtually always scroll/tap, which starts autoplay immediately.
      root.addEventListener('mouseenter', function () { clearInterval(timer); });
      root.addEventListener('mouseleave', function () { if (autoplayStarted) restart(); });
    });
  }

  /* ---- Bundle builder: add all to cart ----------------------------------- */
  function initBundle() {
    document.querySelectorAll('[data-ak-bundle]').forEach(function (root) {
      var btn = root.querySelector('[data-bundle-add]');
      var status = root.querySelector('[data-bundle-status]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var ids = Array.prototype.slice
          .call(root.querySelectorAll('[data-bundle-variant]'))
          .map(function (el) { return el.getAttribute('data-bundle-variant'); })
          .filter(Boolean);
        if (!ids.length) return;
        btn.setAttribute('disabled', 'disabled');
        var items = ids.map(function (id) { return { id: Number(id), quantity: 1 }; });
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            if (status) { status.hidden = false; status.textContent = 'Added to cart!'; }
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') {
              return fetch(window.Shopify.routes.root + '?section_id=cart-drawer')
                .then(function (r) { return r.text(); })
                .then(function () { drawer.open(); document.dispatchEvent(new CustomEvent('cart:refresh')); });
            }
            window.location.href = window.Shopify.routes.root + 'cart';
          })
          .catch(function () {
            if (status) { status.hidden = false; status.textContent = 'Something went wrong. Please try again.'; }
          })
          .finally(function () { btn.removeAttribute('disabled'); });
      });
    });
  }

  /* ---- Mega-menu / dropdown open-on-hover (desktop) ---------------------- */
  function initMegaHover() {
    if (window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(max-width: 989px)').matches) return;
    var details = document.querySelectorAll('.header__inline-menu details');
    details.forEach(function (d) {
      var wrap = d.parentElement; // <header-menu>
      var summary = d.querySelector('summary');
      var closeTimer = null;
      function open() {
        clearTimeout(closeTimer);
        details.forEach(function (o) { if (o !== d) o.open = false; });
        d.open = true;
      }
      function close() {
        closeTimer = setTimeout(function () { d.open = false; }, 120);
      }
      wrap.addEventListener('mouseenter', open);
      wrap.addEventListener('mouseleave', close);
      // Hover drives open/close on desktop; a real mouse click should NOT toggle
      // it shut (matches Hyper). Keyboard (Enter, e.detail === 0) still toggles.
      if (summary) {
        summary.addEventListener('click', function (e) {
          if (e.detail !== 0) { e.preventDefault(); }
        });
      }
    });
  }

  /* ---- Position the full-width predictive search panel below the bar ----- */
  function initSearchOverlay() {
    document.querySelectorAll('.ak-search').forEach(function (ps) {
      var form = ps.querySelector('.ak-search__form');
      var results = ps.querySelector('.ak-search__results');
      var input = ps.querySelector('.ak-search__input');
      var def = ps.querySelector('[data-ak-search-default]');
      if (!form) return;
      function setTop() {
        var b = Math.round(form.getBoundingClientRect().bottom);
        if (results) results.style.top = (b + 8) + 'px';
        if (def) def.style.top = (b + 8) + 'px';
      }
      // Show the trending/popular default panel when the empty search is focused.
      // Robust against late predictive-search upgrade + re-taps on an already-
      // focused field (which fire no 'focus' event): show on focus AND click,
      // and only HIDE on an outside click, Escape, or when the user types.
      function showDefault() {
        if (!def || !input) return;
        if (input.value.trim() === '') { setTop(); def.hidden = false; }
      }
      function hideDefault() { if (def) def.hidden = true; }
      if (input) {
        input.addEventListener('focus', showDefault);
        input.addEventListener('click', showDefault);
        input.addEventListener('input', function () {
          setTop();
          if (input.value.trim() === '') showDefault(); else hideDefault();
        });
      }
      document.addEventListener('click', function (e) {
        // Ignore synthetic clicks (e.isTrusted === false) — carousels/marquees
        // auto-advance by dispatching click events that bubble to document and
        // would otherwise close this panel every few seconds.
        if (!e.isTrusted) return;
        if (def && !ps.contains(e.target)) hideDefault();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') hideDefault();
      });
      window.addEventListener('resize', setTop);
      window.addEventListener('scroll', setTop, { passive: true });
      setTop();
    });
  }

  /* ---- Quick-add prefetch ----------------------------------------------- */
  /* "Choose options" fetches the product page on click, which feels slow.
     Prefetch the product HTML on hover/touchstart so the modal opens instantly. */
  function initQuickAddPrefetch() {
    var prefetched = {};
    function prefetch(url) {
      if (!url || prefetched[url]) return;
      prefetched[url] = true;
      fetch(url, { priority: 'low' }).catch(function () { prefetched[url] = false; });
    }
    function handler(e) {
      var opener = e.target.closest('.quick-add__submit[data-product-url], modal-opener [data-product-url], .card__heading a, .ak-ranked__title');
      if (!opener) return;
      var url = opener.getAttribute('data-product-url') || opener.getAttribute('href');
      if (url && url.indexOf('/products/') !== -1) prefetch(url);
    }
    document.addEventListener('mouseover', handler, { passive: true });
    document.addEventListener('touchstart', handler, { passive: true });
  }

  /* ---- Infinite scroll for collection / search product grids ------------- */
  /* Auto-loads the next page into #product-grid as the user nears the bottom,
     replacing numeric pagination. Falls back to normal pagination if JS fails. */
  function initInfiniteScroll() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var pagination = document.querySelector('.pagination');
    if (!pagination) return; // single page — nothing to load
    var loadingText = document.documentElement.getAttribute('data-loading') || 'Loading more…';

    // hide numeric pagination once JS takes over
    pagination.style.display = 'none';

    var url = new URL(window.location.href);
    var page = parseInt(url.searchParams.get('page') || '1', 10);
    var done = false;
    var loading = false;

    var sentinel = document.createElement('div');
    sentinel.className = 'ak-infinite-sentinel';
    sentinel.innerHTML = '<span class="ak-infinite-spinner" hidden>' + loadingText + '</span>';
    grid.parentNode.insertBefore(sentinel, grid.nextSibling);
    var spinner = sentinel.querySelector('.ak-infinite-spinner');

    function load() {
      if (done || loading) return;
      loading = true;
      if (spinner) spinner.hidden = false;
      var u = new URL(window.location.href);
      u.searchParams.set('page', page + 1);
      fetch(u.toString())
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('#product-grid > li');
          if (!items.length) { done = true; if (spinner) spinner.hidden = true; io.disconnect(); return; }
          items.forEach(function (li) { grid.appendChild(document.importNode(li, true)); });
          page += 1;
          // Shopify serves the LAST page's products for any page beyond the range,
          // so items.length is never 0 and this looped forever — re-appending the
          // final page and re-parsing ~700KB documents until the renderer OOMed
          // ("Aw, Snap" on scroll). A page is the last iff it has no next link.
          if (!doc.querySelector('link[rel="next"], .pagination__item--next')) {
            done = true; io.disconnect();
          }
          loading = false;
          if (spinner) spinner.hidden = true;
          // update URL so refresh/share keeps position-ish, without reloading
          history.replaceState(null, '', u.toString());
        })
        .catch(function () { loading = false; if (spinner) spinner.hidden = true; });
    }

    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) load();
    }, {
      // 1400px, not 700: the fetch must land while the sentinel is still well off
      // screen. At 700px the appended row arrived with the sentinel in view, so it
      // pushed visible content down = layout shift on every page load.
      rootMargin: '1400px 0px'
    });
    io.observe(sentinel);
  }

  /* ---- Sticky add-to-cart bar (product page) ----------------------------- */
  /* Slides up whenever the real Add-to-Cart button is out of reach, and stays up.
     Proxies clicks to the real product form. */
  function initStickyAtc() {
    var bar = document.querySelector('[data-ak-sticky-atc]');
    if (!bar) return;
    var realBtn = document.querySelector('.product-form__submit');
    if (!realBtn) return;
    var addBtn = bar.querySelector('[data-ak-sticky-add]');
    var defaultLabel = addBtn ? (addBtn.textContent || 'Add to cart').trim() : 'Add to cart';
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    function openDrawer() {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = root + 'cart';
    }
    function setView(on) {
      if (!addBtn) return;
      addBtn.dataset.akView = on ? '1' : '';
      addBtn.textContent = on ? 'View cart' : defaultLabel;
    }
    var pm = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    var handle = pm ? pm[1] : null;
    function getCart() {
      return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return { items: [], item_count: 0 }; });
    }
    // "View cart" only once THIS product is in the cart (not other items).
    function hasThisProduct(cart) {
      if (handle && cart && cart.items) {
        return cart.items.some(function (it) { return it.handle === handle; });
      }
      return !!(cart && cart.item_count > 0);
    }
    function syncFromCart() { getCart().then(function (c) { setView(hasThisProduct(c)); }); }

    // Reflect the current cart on load, and keep in sync whenever the cart
    // changes (Dawn replaces the header bubble's contents on every update).
    syncFromCart();
    document.addEventListener('cart:refresh', syncFromCart);
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && window.MutationObserver) {
      new MutationObserver(syncFromCart).observe(bubble, { childList: true, subtree: true });
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        // Once this product is in the cart the button becomes a "View cart" shortcut.
        if (addBtn.dataset.akView === '1') { openDrawer(); return; }
        if (realBtn.getAttribute('aria-disabled') === 'true' || realBtn.disabled) return;
        // Proxy the add through the real form (Dawn runs its AJAX add + opens the
        // drawer), then flip to "View cart" once the cart count actually grows.
        getCart().then(function (before) {
          var n0 = (before && before.item_count) || 0;
          realBtn.click();
          var tries = 0;
          var iv = setInterval(function () {
            tries += 1;
            getCart().then(function (now) {
              if (((now && now.item_count) || 0) > n0) { clearInterval(iv); syncFromCart(); }
              else if (tries >= 12) { clearInterval(iv); syncFromCart(); }
            });
          }, 300);
        });
      });
    }
    var ticking = false;
    var footer = document.querySelector('.ak-footer');
    var mqNarrow = window.matchMedia('(max-width: 749px)');
    function onScroll() {
      ticking = false;
      var y = window.scrollY;
      var r = realBtn.getBoundingClientRect();
      // The bar exists to keep a reachable Add-to-cart on screen, so show it
      // whenever the real button is NOT reachable: either scrolled off the top,
      // or — on mobile, where the title, price and ATC all sit below the fold —
      // still further down the page than the viewport.
      var scrolledOffTop = r.bottom < 10;
      var stillBelowViewport = mqNarrow.matches && r.top > window.innerHeight && y > 300;
      // Deliberately direction-independent. The previous rule only showed the bar
      // while actively scrolling DOWN and hid it on any scroll UP, which removed
      // the only mobile buy button the moment a shopper scrolled back to re-check
      // a photo — the most common thing people do on a product page.
      // Hide the bar once the footer scrolls into view. The bar is fixed at the
      // bottom (z-index 30) and would otherwise sit on top of the footer links,
      // swallowing taps on them so the page never navigates (product pages only).
      var footerInView = footer && footer.getBoundingClientRect().top < window.innerHeight;
      bar.classList.toggle('is-visible', !footerInView && (scrolledOffTop || stillBelowViewport));
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
  }

  function init() {
    window.AKCarousel = initProductCarousel;
    initSlider();
    initReveal();
    initMegaHover();
    initSearchOverlay();
    initQuickAddPrefetch();
    /* Everything below the fold waits for idle: initProductCarousel reads
       scrollWidth/clientWidth per carousel, forcing synchronous layout before LCP. */
    var deferred = function () {
      initTabs();
      initProductCarousel();
      initStickyAtc();
      initCountdown();
      initBundle();
      initInfiniteScroll();
    };
    if ('requestIdleCallback' in window) requestIdleCallback(deferred, { timeout: 2500 });
    else setTimeout(deferred, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   Quick-add modal gallery — Dawn's <media-gallery> doesn't initialize inside the
   dynamically-injected quick-add modal, so we drive a PDP-style gallery ourselves:
   one active main image + a thumbnail strip; clicking a thumbnail switches the
   active image. Pairs with the .quick-add-modal gallery CSS in nestaria.css.
   ========================================================================== */
(function () {
  function setup(modal) {
    var content = modal.querySelector('.quick-add-modal__content');
    if (!content) return;
    var mainItems = content.querySelectorAll('.product__media-list .product__media-item');
    if (!mainItems.length) return;
    var anyActive = false;
    mainItems.forEach(function (m) { if (m.classList.contains('is-active')) anyActive = true; });
    if (!anyActive) mainItems[0].classList.add('is-active');
    var thumbs = content.querySelectorAll('.thumbnail-list__item');
    thumbs.forEach(function (t, i) {
      var b = t.querySelector('button');
      if (b) b.setAttribute('aria-current', i === 0 && !anyActive ? 'true' : b.getAttribute('aria-current') || 'false');
    });
    addArrows(content);
  }
  function currentIdx(content) {
    var items = [].slice.call(content.querySelectorAll('.product__media-list .product__media-item'));
    for (var i = 0; i < items.length; i++) if (items[i].classList.contains('is-active')) return i;
    return 0;
  }
  function addArrows(content) {
    var mainSlider = content.querySelector('.product__media-list');
    if (!mainSlider) return;
    var wrap = mainSlider.parentElement; // slider-component (main)
    if (!wrap || wrap.querySelector('.ak-qa-nav')) return;
    var count = content.querySelectorAll('.product__media-list .product__media-item').length;
    if (count < 2) return;
    ['prev', 'next'].forEach(function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ak-qa-nav ak-qa-nav--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
      b.innerHTML = dir === 'prev' ? '‹' : '›';
      b.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var n = content.querySelectorAll('.product__media-list .product__media-item').length;
        var cur = currentIdx(content);
        switchTo(content, (cur + (dir === 'prev' ? -1 : 1) + n) % n);
      });
      wrap.appendChild(b);
    });
  }
  function switchTo(content, idx) {
    var mainItems = content.querySelectorAll('.product__media-list .product__media-item');
    var thumbs = content.querySelectorAll('.thumbnail-list__item');
    mainItems.forEach(function (m, i) { m.classList.toggle('is-active', i === idx); });
    thumbs.forEach(function (t, i) {
      var b = t.querySelector('button');
      if (b) b.setAttribute('aria-current', i === idx ? 'true' : 'false');
      var th = t.querySelector('.thumbnail');
      if (th) th.setAttribute('aria-current', i === idx ? 'true' : 'false');
    });
  }
  // click a thumbnail in the modal -> switch the main image
  document.addEventListener('click', function (e) {
    var modal = e.target.closest('.quick-add-modal');
    if (!modal) return;
    var item = e.target.closest('.thumbnail-list__item');
    if (!item) return;
    e.preventDefault();
    e.stopPropagation();
    var content = modal.querySelector('.quick-add-modal__content');
    var thumbs = [].slice.call(content.querySelectorAll('.thumbnail-list__item'));
    var idx = thumbs.indexOf(item);
    if (idx >= 0) switchTo(content, idx);
  }, true);
  // when a quick-add modal is populated, set the first image active
  var mo = new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      var t = m.target;
      var modal = t.closest ? t.closest('.quick-add-modal') : null;
      if (modal) setup(modal);
    });
  });
  function observeModals() {
    document.querySelectorAll('quick-add-modal, .quick-add-modal').forEach(function (qm) {
      if (qm.dataset.akGalObs) return;
      qm.dataset.akGalObs = '1';
      mo.observe(qm, { childList: true, subtree: true });
    });
  }
  if (document.readyState !== 'loading') observeModals();
  else document.addEventListener('DOMContentLoaded', observeModals);
  setTimeout(observeModals, 1500);
  // Modals inside async-loaded sections (related products) aren't observed yet,
  // so when a "Choose options" opener is clicked, poll for the populated modal
  // and run setup() (set first image active + inject prev/next arrows).
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.quick-add__submit, modal-opener, [aria-haspopup="dialog"]')) return;
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var populated = [].slice.call(document.querySelectorAll('.quick-add-modal'))
        .filter(function (m) { return m.querySelector('.product__media-list .product__media-item'); })[0];
      // keep re-running setup() (idempotent) for a few seconds so the arrows
      // survive any re-render of the modal content after injection.
      if (populated) setup(populated);
      if (tries > 30) clearInterval(iv);
    }, 250);
  });
})();
